/**
 * Llogaritë bankare të punëtorit — historik, jo një fushë e vetme.
 *
 * Asnjë llogari nuk fshihet: kur punëtori ndërron bankën, shtohet llogaria e re
 * dhe e vjetra çaktivizohet por mbetet e dukshme. Vetëm njëra është aktive — ajo
 * ku shkon paga (dhe që lexojnë listat e pagave e eksportet bankare).
 */
import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { alpha, useTheme } from '@mui/material/styles';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import { authFetch } from '../../services/apiServices';

const fmtDate = (v) => {
  if (!v) return '';
  const s = String(v).slice(0, 10);
  const [y, m, d] = s.split('-');
  return d ? `${d}.${m}.${y}` : s;
};

async function call(path, options) {
  const r = await authFetch(path, options);
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
  return j.data;
}

export default function WorkerBankAccounts({ workerId, canEdit = true, onActiveChange }) {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ bank_name: '', account_number: '', active_from: '', make_active: true });

  const load = useCallback(async () => {
    if (!workerId) return;
    setLoading(true); setError('');
    try { setRows(await call(`/api/workers/${workerId}/bank-accounts`)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [workerId]);
  useEffect(() => { load(); }, [load]);

  const applyResult = (data) => {
    const list = data?.accounts || [];
    setRows(list);
    const active = list.find((a) => a.IsActive);
    onActiveChange?.(active || null);
  };

  const add = async () => {
    if (!form.account_number.trim()) { setError('Shkruaj numrin e llogarisë'); return; }
    setBusy(true); setError('');
    try {
      applyResult(await call(`/api/workers/${workerId}/bank-accounts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, active_from: form.active_from || null }),
      }));
      setAdding(false);
      setForm({ bank_name: '', account_number: '', active_from: '', make_active: true });
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const setActive = async (acc, isActive) => {
    setBusy(true); setError('');
    try {
      applyResult(await call(`/api/workers/${workerId}/bank-accounts/${acc.Id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: isActive }),
      }));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const active = rows.find((a) => a.IsActive);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {active
            ? <>Paga shkon te <b>{active.BankName || 'llogaria'} · {active.AccountNumber}</b></>
            : 'Asnjë llogari aktive — paga s\'ka ku të shkojë'}
        </Typography>
        {canEdit && !adding && (
          <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => setAdding(true)} disabled={busy}>
            Shto llogari
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError('')}>{error}</Alert>}

      {adding && (
        <Box sx={{ p: 1.25, mb: 1, border: 1, borderColor: 'divider', borderRadius: 1.5, bgcolor: alpha(theme.palette.info.main, 0.04) }}>
          <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '1fr 1.4fr 0.9fr' } }}>
            <TextField size="small" label="Emri i bankës" value={form.bank_name}
              onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))} />
            <TextField size="small" label="Xhirollogaria / IBAN" required autoFocus value={form.account_number}
              onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))} />
            <TextField size="small" type="date" label="Aktive nga" InputLabelProps={{ shrink: true }} value={form.active_from}
              onChange={(e) => setForm((f) => ({ ...f, active_from: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75, gap: 1, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={form.make_active} onChange={(e) => setForm((f) => ({ ...f, make_active: e.target.checked }))} />}
              label={<Typography variant="caption">Bëje aktive (paga shkon këtu; e mëparshmja mbetet në historik)</Typography>}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={() => setAdding(false)} disabled={busy}>Anulo</Button>
              <Button size="small" variant="contained" onClick={add} disabled={busy}>Ruaj llogarinë</Button>
            </Box>
          </Box>
        </Box>
      )}

      {loading ? (
        <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>Nuk ka ende asnjë llogari bankare.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {rows.map((a) => (
            <Box key={a.Id} sx={{
              display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 0.9, borderRadius: 1.5,
              border: 1, borderColor: a.IsActive ? alpha(theme.palette.success.main, 0.5) : 'divider',
              bgcolor: a.IsActive ? alpha(theme.palette.success.main, 0.06) : 'transparent',
              opacity: a.IsActive ? 1 : 0.85,
            }}>
              <AccountBalanceRoundedIcon sx={{ fontSize: 20, color: a.IsActive ? 'success.main' : 'text.disabled' }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {a.AccountNumber}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {a.BankName || 'Banka e pacaktuar'}
                  {a.ActiveFrom ? ` · nga ${fmtDate(a.ActiveFrom)}` : ''}
                  {!a.IsActive && a.DeactivatedAt ? ` · çaktivizuar ${fmtDate(a.DeactivatedAt)}` : ''}
                </Typography>
              </Box>
              {a.IsActive
                ? <Chip size="small" color="success" icon={<CheckCircleRoundedIcon />} label="Aktive" />
                : <Chip size="small" variant="outlined" label="Joaktive" />}
              {canEdit && (a.IsActive ? (
                <Tooltip title="Paga s'do të ketë llogari derisa të aktivizohet një tjetër">
                  <span><Button size="small" color="inherit" disabled={busy} onClick={() => setActive(a, false)}>Çaktivizo</Button></span>
                </Tooltip>
              ) : (
                <Button size="small" disabled={busy} onClick={() => setActive(a, true)}>Bëje aktive</Button>
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
